import { createHash } from 'crypto';

import { LRUCache } from 'lru-cache';
// @ts-ignore
import type * as RCL from 'rclnodejs';

import type { CartesianPose } from '@sb/geometry';
import { ros2TooltipToTarget, targetToROS2Tooltip } from '@sb/geometry';
import { makeNamespacedLog } from '@sb/log';
import type {
  ForwardKinematicsRequest,
  ForwardKinematicsResponse,
  FrameOfReference,
  InverseKinematicsRequest,
  InverseKinematicsSolution,
} from '@sb/motion-planning';
import { ArmJointPositions } from '@sb/motion-planning';
import type { CartesianPlanningActionRequest } from '@sb/ros/CartesianPlanningAction';
import type { TaskFromRobotGoalActionRequest } from '@sb/ros/TaskFromRobotGoalAction';
import { TaskFromRobotGoalAction } from '@sb/ros/TaskFromRobotGoalAction';
import { TaskFromRobotGoalActionResult } from '@sb/ros/types/TaskFromRobotGoalActionResult';
import type { CacheBackend } from '@sb/utilities/src/ReadThroughCache';
import { ReadThroughCacheAsync } from '@sb/utilities/src/ReadThroughCacheAsync';

import { CartesianPlanningAction } from './CartesianPlanningAction';
import { FKCheckService } from './FKCheckService';
import {
  getMotionPlanBucketKey,
  getRosMotionPlanCacheKey,
} from './getRosMotionPlanCacheyKey';
import {
  GetToolDirectionsService,
  type FrameDirections,
} from './GetToolDirectionsService';
import { IKCollisionCheckService } from './IKCollisionCheckService';
import { JoggingAction, type JoggingActionRequest } from './JoggingAction';
import { RecoveryService } from './RecoveryService';
import type { CallableCancellableAction } from './types/CallableCancellableAction';

const log = makeNamespacedLog('RosMotionPlanner');

export const DEFAULT_START_JOINT_TOLERANCE = 0.002;
const DEFAULT_BUCKET_SIZE = 1000;
const DEFAULT_BUCKET_ENTRY_CAPACITY = 1000;

type MotionPlanBucketEntry = {
  cacheKey: string;
  request: MotionPlanCacheRequest;
};

type MotionPlanBucketOptions = {
  bucketSize?: number;
  startJointTolerance?: number;
  isWithinStartJointTolerance?: (
    cachedStart: number[],
    requestedStart: number[],
  ) => boolean;
};

type MotionPlanCacheRequest = RosMotionPlannerRequest & {
  useScalingForSpeedLimits: boolean;
};

export type RosMotionPlannerRequest = (
  | TaskFromRobotGoalActionRequest
  | CartesianPlanningActionRequest
) & { useCartesianPlanner: boolean };

export class RosMotionPlanner {
  private motionPlanCache: ReadThroughCacheAsync<
    RosMotionPlannerRequest,
    TaskFromRobotGoalActionResult
  > | null = null;

  private motionPlanBucketCache: LRUCache<
    string,
    LRUCache<string, MotionPlanBucketEntry>
  > | null = null;

  private readonly isWithinStartJointTolerance: (
    cachedStart: number[],
    requestedStart: number[],
  ) => boolean;

  private bucketGetCount = 0;

  private bucketHitCount = 0;

  public constructor(
    motionPlanCacheBackend: CacheBackend<TaskFromRobotGoalActionResult> | null = null,
    options: MotionPlanBucketOptions = {},
  ) {
    const startJointTolerance =
      options.startJointTolerance ?? DEFAULT_START_JOINT_TOLERANCE;

    this.isWithinStartJointTolerance =
      options.isWithinStartJointTolerance ??
      ((cachedStart, requestedStart) =>
        this.areJointStatesWithinTolerance(
          cachedStart,
          requestedStart,
          startJointTolerance,
        ));

    if (motionPlanCacheBackend) {
      this.motionPlanCache = new ReadThroughCacheAsync<
        RosMotionPlannerRequest,
        TaskFromRobotGoalActionResult
      >({
        getValue: (request: RosMotionPlannerRequest) => {
          const planAction: CallableCancellableAction<TaskFromRobotGoalActionResult> =
            request.useCartesianPlanner
              ? new CartesianPlanningAction(
                  request as CartesianPlanningActionRequest,
                )
              : new TaskFromRobotGoalAction(
                  request as TaskFromRobotGoalActionRequest,
                );

          return {
            call: async () => {
              const planResult = await planAction.call();

              return TaskFromRobotGoalActionResult.parse(planResult);
            },
            cancel: () => planAction.cancel(),
          };
        },
        getCacheKey: (req) => getRosMotionPlanCacheKey(req),
        cacheBackend: motionPlanCacheBackend,
      });

      const inferredBucketSize =
        options.bucketSize ??
        Math.max(DEFAULT_BUCKET_SIZE, motionPlanCacheBackend.size());

      this.motionPlanBucketCache = new LRUCache<
        string,
        LRUCache<string, MotionPlanBucketEntry>
      >({
        max: inferredBucketSize,
        dispose: (bucketEntries, _, reason) => {
          if (reason === 'evict') {
            this.onBucketEvicted(bucketEntries);
          }
        },
      });

      this.initializeBucketCacheFromBackend(motionPlanCacheBackend);
    } else {
      this.motionPlanCache = null;
      this.motionPlanBucketCache = null;
    }
  }

  private forwardKinematicsCache = new LRUCache<
    string,
    ForwardKinematicsResponse
  >({ max: 50 });

  public async forwardKinematics(
    request: ForwardKinematicsRequest,
  ): Promise<ForwardKinematicsResponse> {
    const formatPosition = (position?: { x: number; y: number; z: number }) =>
      [position?.x ?? 0, position?.y ?? 0, position?.z ?? 0]
        .map((value) => value.toFixed(4))
        .join('|');

    const key = [
      request.jointAngles.map((angle) => angle.toFixed(4)).join('|'),
      request.checkValidity ? '1' : '0',
      request.gripperOpenness.toFixed(2),
      request.checkEeBounds ? '1' : '0',
      request.checkBodyBounds ? '1' : '0',
      formatPosition(request.eePositionMin),
      formatPosition(request.eePositionMax),
      formatPosition(request.bodyPositionMin),
      formatPosition(request.bodyPositionMax),
    ].join(':');

    const cachedKinematics = this.forwardKinematicsCache.get(key);

    if (cachedKinematics) {
      return cachedKinematics;
    }

    const jointState = {
      header: {
        stamp: { sec: 0, nanosec: 0 },
        frame_id: 'world',
      },
      name: ['joint0', 'joint1', 'joint2', 'joint3', 'joint4', 'joint5'],
      position: request.jointAngles,
      velocity: [],
      effort: [],
    };

    const fk_service = new FKCheckService(jointState, request.checkValidity, {
      checkEeBounds: request.checkEeBounds ?? false,
      checkBodyBounds: request.checkBodyBounds ?? false,
      eePositionMin: request.eePositionMin,
      eePositionMax: request.eePositionMax,
      bodyPositionMin: request.bodyPositionMin,
      bodyPositionMax: request.bodyPositionMax,
    });

    const error = new Error();

    try {
      const response = await fk_service.call();

      if (!response.success) {
        throw new Error(
          `ROS2 Forward kinematics failed on joint state: ${JSON.stringify(jointState)}`,
        );
      }

      const pose: CartesianPose = ros2TooltipToTarget({
        x: response.pose.position.x,
        y: response.pose.position.y,
        z: response.pose.position.z,
        i: response.pose.orientation.x,
        j: response.pose.orientation.y,
        k: response.pose.orientation.z,
        w: response.pose.orientation.w,
      });

      const collisions = (response.collisions ?? []).map(
        (collision: RCL.standard_bots_msgs.msg.Collision) => ({
          elementNames: [collision.body_1, collision.body_2].sort() as [
            string,
            string,
          ],
        }),
      );

      const result: ForwardKinematicsResponse = {
        pose,
        collisions,
        isColliding: response.in_collision,
        isEeOutOfBounds: response.is_ee_out_of_bounds ?? false,
        isBodyOutOfBounds: response.is_body_out_of_bounds ?? false,
      };

      this.forwardKinematicsCache.set(key, result);

      return result;
    } catch (e) {
      error.message = `Forward kinematics failed: ${e?.message}`;
      throw error;
    }
  }

  /**
   * Drop all cached FK responses. Call when scene-affecting state (e.g. the
   * planning-scene ACM) changes so the next FK round-trips to the planner
   * instead of returning a result computed against the old scene.
   */
  public clearForwardKinematicsCache(): void {
    this.forwardKinematicsCache.clear();
  }

  public async inverseKinematics(
    request: InverseKinematicsRequest,
  ): Promise<Array<InverseKinematicsSolution>> {
    const error = new Error();
    const tooltipPose = targetToROS2Tooltip(request.pose);

    const goal_pose: RCL.geometry_msgs.msg.Pose = {
      position: {
        x: tooltipPose.x,
        y: tooltipPose.y,
        z: tooltipPose.z,
      },
      orientation: {
        x: tooltipPose.i,
        y: tooltipPose.j,
        z: tooltipPose.k,
        w: tooltipPose.w,
      },
    };

    const ik_service = new IKCollisionCheckService(
      goal_pose,
      request.checkValidity,
      request.jointSeed,
    );

    const response = await ik_service.call();

    if (!response.success) {
      error.message = 'Inverse kinematics failed';
      throw error;
    }

    const jointPositions = Array.from(response.ik_solution.position).slice(
      0,
      6,
    );

    // Validate and parse the joint positions
    let jointAngles: ArmJointPositions;

    try {
      jointAngles = ArmJointPositions.parse(jointPositions);
    } catch (e) {
      error.message = `Failed to parse joint positions: ${e.message}`;
      throw error;
    }

    const solution: InverseKinematicsSolution = {
      jointAngles,
      collisions: [], // IKCollisionCheck_Response doesn't provide detailed collision information
      isColliding: response.in_collision,
      violatesLimits: false, // IKCollisionCheck_Response doesn't provide joint limit information
      limitViolations: new Array(6).fill(null), // Assuming 6 joints, fill with null as this info is not provided
    };

    return [solution];
  }

  public async getMotionPlan(
    request: RosMotionPlannerRequest,
    useCache = true,
    useScalingForSpeedLimits = false,
  ): Promise<TaskFromRobotGoalActionResult> {
    if (!this.motionPlanCache) {
      throw new Error('Motion plan cache not initialized');
    }

    if (useScalingForSpeedLimits) {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using scaling for speed limits',
      );
    } else {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using individual joint limits for speed limits',
      );
    }

    const requestWithScaling: MotionPlanCacheRequest = {
      ...request,
      useScalingForSpeedLimits,
    };

    if (!useCache) {
      log.info(`RosMotionPlanner.getMotionPlan`, 'skipping cache');

      return this.motionPlanCache.getSkipCache(requestWithScaling);
    }

    const bucketMatch = this.tryGetBucketMatch(requestWithScaling);

    if (bucketMatch) {
      const plan = await this.motionPlanCache.get(bucketMatch.request);

      const cachedPlanKey = getRosMotionPlanCacheKey(requestWithScaling);

      if (cachedPlanKey !== bucketMatch.cacheKey) {
        this.motionPlanCache.set(requestWithScaling, plan);
        this.storeRequestInBucket(requestWithScaling);
      }

      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'planMotion cache stats',
        this.getCacheStats(),
      );

      return plan;
    }

    const response = await this.motionPlanCache.get(requestWithScaling);

    this.storeRequestInBucket(requestWithScaling);

    log.info(
      `RosMotionPlanner.getMotionPlan`,
      'planMotion cache stats',
      this.getCacheStats(),
    );

    return response;
  }

  public getMotionPlanCancellable(
    request: RosMotionPlannerRequest,
    useCache = true,
    useScalingForSpeedLimits = false,
  ): CallableCancellableAction<TaskFromRobotGoalActionResult> {
    if (!this.motionPlanCache) {
      throw new Error('Motion plan cache not initialized');
    }

    if (useScalingForSpeedLimits) {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using scaling for speed limits',
      );
    } else {
      log.info(
        `RosMotionPlanner.getMotionPlan`,
        'using individual joint limits for speed limits',
      );
    }

    const requestWithScaling: MotionPlanCacheRequest = {
      ...request,
      useScalingForSpeedLimits,
    };

    if (!useCache) {
      log.info(`RosMotionPlanner.getMotionPlan`, 'Skipping cache');

      return this.motionPlanCache.getCancellableSkipCache(requestWithScaling);
    }

    const bucketMatch = this.tryGetBucketMatch(requestWithScaling);

    if (bucketMatch) {
      const cancellable = this.motionPlanCache.getCancellable(
        bucketMatch.request,
      );

      return {
        call: async () => {
          const plan = await cancellable.call();

          const cachedPlanKey = getRosMotionPlanCacheKey(requestWithScaling);

          if (cachedPlanKey !== bucketMatch.cacheKey && this.motionPlanCache) {
            this.motionPlanCache.set(requestWithScaling, plan);
            this.storeRequestInBucket(requestWithScaling);
          }

          log.info(
            `RosMotionPlanner.getMotionPlan`,
            'planMotion cache stats',
            this.getCacheStats(),
          );

          return plan;
        },
        cancel: () => cancellable.cancel(),
      };
    }

    const callableCancellableAction =
      this.motionPlanCache.getCancellable(requestWithScaling);

    return {
      call: async () => {
        const plan = await callableCancellableAction.call();
        this.storeRequestInBucket(requestWithScaling);

        log.info(
          `RosMotionPlanner.getMotionPlan`,
          'planMotion cache stats',
          this.getCacheStats(),
        );

        return plan;
      },
      cancel: () => callableCancellableAction.cancel(),
    };
  }

  private tryGetBucketMatch(
    request: MotionPlanCacheRequest,
  ): MotionPlanBucketEntry | null {
    if (!this.motionPlanBucketCache) {
      return null;
    }

    if (!Array.isArray(request.startJointState)) {
      return null;
    }

    this.bucketGetCount += 1;

    const bucketKey = getMotionPlanBucketKey(request);
    const bucketEntries = this.motionPlanBucketCache.get(bucketKey);

    // print md5 sum of bucket key
    const md5 = createHash('md5').update(bucketKey).digest('hex');
    log.info(`RosMotionPlanner.tryGetBucketMatch`, 'bucketKey md5', md5);

    if (!bucketEntries || bucketEntries.size === 0) {
      log.info(`RosMotionPlanner.tryGetBucketMatch`, 'no bucket entries found');

      return null;
    }

    const startJointState = Array.from(request.startJointState);

    let matchedCacheKey: string | null = null;

    for (const [entryCacheKey, entry] of bucketEntries.entries()) {
      // Type guard to ensure entry is MotionPlanBucketEntry
      if (
        entry &&
        typeof entry === 'object' &&
        'request' in entry &&
        'cacheKey' in entry &&
        Array.isArray(entry.request.startJointState) &&
        this.isWithinStartJointTolerance(
          entry.request.startJointState,
          startJointState,
        )
      ) {
        matchedCacheKey = entryCacheKey as string;
        break;
      }
    }

    if (!matchedCacheKey) {
      log.info(`RosMotionPlanner.tryGetBucketMatch`, 'no matching entry found');

      return null;
    }

    const matchedEntry = bucketEntries.get(matchedCacheKey);

    if (!matchedEntry) {
      log.info(
        `RosMotionPlanner.tryGetBucketMatch`,
        'matching entry missing after lookup',
      );

      return null;
    }

    log.info(`RosMotionPlanner.tryGetBucketMatch`, 'matching entry found');

    this.bucketHitCount += 1;

    return matchedEntry;
  }

  private storeRequestInBucket(request: MotionPlanCacheRequest): void {
    if (!this.motionPlanBucketCache) {
      return;
    }

    if (!Array.isArray(request.startJointState)) {
      return;
    }

    const entry: MotionPlanBucketEntry = {
      cacheKey: getRosMotionPlanCacheKey(request),
      request: this.cloneRequestForBucket(request),
    };

    const bucketKey = getMotionPlanBucketKey(request);

    this.upsertBucketEntry(bucketKey, entry);
  }

  private upsertBucketEntry(
    bucketKey: string,
    entry: MotionPlanBucketEntry,
  ): void {
    if (!this.motionPlanBucketCache) {
      return;
    }

    let bucketEntries = this.motionPlanBucketCache.get(bucketKey);

    if (!bucketEntries) {
      bucketEntries = this.createBucketEntryCache();
      this.motionPlanBucketCache.set(bucketKey, bucketEntries);
    }

    bucketEntries.set(entry.cacheKey, entry);
  }

  private createBucketEntryCache(): LRUCache<string, MotionPlanBucketEntry> {
    return new LRUCache<string, MotionPlanBucketEntry>({
      max: DEFAULT_BUCKET_ENTRY_CAPACITY,
      dispose: (_, cacheKey, reason) => {
        if (reason === 'evict') {
          this.deleteMotionPlanCacheEntry(
            cacheKey,
            `RosMotionPlanner.bucketEntryEvicted`,
          );
        }
      },
    });
  }

  private initializeBucketCacheFromBackend(
    backend: CacheBackend<TaskFromRobotGoalActionResult>,
  ): void {
    if (!this.motionPlanBucketCache || typeof backend.keys !== 'function') {
      return;
    }

    try {
      const cacheKeys = Array.from(backend.keys());

      for (const cacheKey of cacheKeys) {
        if (typeof cacheKey !== 'string') {
          continue;
        }

        const restoredRequest = this.restoreRequestFromCacheKey(cacheKey);

        if (!restoredRequest) {
          continue;
        }

        const motionPlan = backend.get(cacheKey);

        if (!motionPlan) {
          continue;
        }

        const canonicalKey = getRosMotionPlanCacheKey(restoredRequest);

        // this.motionPlanCache?.set(restoredRequest, motionPlan);
        // if (canonicalKey !== cacheKey) {
        //   this.motionPlanCache?.deleteByCacheKey(cacheKey);
        // }

        const bucketKey = getMotionPlanBucketKey(restoredRequest);

        this.upsertBucketEntry(bucketKey, {
          cacheKey: canonicalKey,
          request: this.cloneRequestForBucket(restoredRequest),
        });
      }
    } catch (error) {
      log.warn(
        `RosMotionPlanner.initializeBucketCacheFromBackend`,
        'failed to seed bucket cache',
        error,
      );
    }
  }

  private onBucketEvicted(
    bucketEntries?: LRUCache<string, MotionPlanBucketEntry>,
  ): void {
    if (!bucketEntries) {
      return;
    }

    for (const entry of bucketEntries.values()) {
      // Type guard to ensure entry is MotionPlanBucketEntry
      if (entry && typeof entry === 'object' && 'cacheKey' in entry) {
        this.deleteMotionPlanCacheEntry(
          entry.cacheKey,
          `RosMotionPlanner.onBucketEvicted`,
        );
      }
    }
  }

  private deleteMotionPlanCacheEntry(
    cacheKey: string,
    logContext: string,
  ): void {
    if (!this.motionPlanCache) {
      return;
    }

    try {
      this.motionPlanCache.deleteByCacheKey(cacheKey);
    } catch (error) {
      log.debug(logContext, 'failed to evict entry-level cache key', {
        cacheKey,
        error,
      });
    }
  }

  private areJointStatesWithinTolerance(
    cachedStart: number[],
    requestedStart: number[],
    tolerance: number,
  ): boolean {
    if (cachedStart.length !== requestedStart.length) {
      return false;
    }

    return cachedStart.every(
      (cachedValue, index) =>
        Math.abs(cachedValue - requestedStart[index]) <= tolerance,
    );
  }

  private parseJointArray(value: unknown): number[] | null {
    if (Array.isArray(value)) {
      const numbers = value.map((joint) => Number(joint));

      return numbers.some((joint) => Number.isNaN(joint)) ? null : numbers;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
        return null;
      }

      const inner = trimmed.slice(1, -1).trim();

      if (!inner) {
        return [];
      }

      const numbers = inner.split(',').map((part) => Number(part.trim()));

      return numbers.some((joint) => Number.isNaN(joint)) ? null : numbers;
    }

    return null;
  }

  private cloneRequestForBucket(
    request: MotionPlanCacheRequest,
  ): MotionPlanCacheRequest {
    return JSON.parse(JSON.stringify(request)) as MotionPlanCacheRequest;
  }

  private restoreRequestFromCacheKey(
    cacheKey: string,
  ): MotionPlanCacheRequest | null {
    try {
      const parsed = JSON.parse(cacheKey);

      const startJointState = this.parseJointArray(parsed.startJointState);

      if (!startJointState) {
        return null;
      }

      parsed.startJointState = startJointState;

      if (Array.isArray(parsed.targets)) {
        parsed.targets = parsed.targets.map((target: any) => {
          if (target && typeof target === 'object' && 'jointAngles' in target) {
            const jointAngles = this.parseJointArray(target.jointAngles);

            if (jointAngles) {
              return { ...target, jointAngles };
            }
          }

          return target;
        });
      }

      if (Array.isArray(parsed.speedProfiles)) {
        parsed.speedProfiles = parsed.speedProfiles.map((profile: any) => ({
          ...profile,
        }));
      } else if (parsed.speedProfile) {
        parsed.speedProfiles = [parsed.speedProfile];
        delete parsed.speedProfile;
      } else {
        parsed.speedProfiles = [];
      }

      parsed.useScalingForSpeedLimits = Boolean(
        parsed.useScalingForSpeedLimits,
      );

      return parsed as MotionPlanCacheRequest;
    } catch (error) {
      log.debug(
        `RosMotionPlanner.restoreRequestFromCacheKey`,
        'failed to restore request from cache key',
        { error, cacheKey },
      );

      return null;
    }
  }

  private getCacheStats() {
    const hitCount = this.motionPlanCache?.hitCount ?? 0;
    const getCount = this.motionPlanCache?.getCount ?? 0;
    const cacheSize = this.motionPlanCache?.size() ?? 0;
    const bucketSize = this.motionPlanBucketCache?.size ?? 0;

    const hitRate = getCount === 0 ? '0' : (hitCount / getCount).toPrecision(4);

    const bucketHitRate =
      this.bucketGetCount === 0
        ? '0'
        : (this.bucketHitCount / this.bucketGetCount).toPrecision(4);

    return {
      hitCount,
      getCount,
      hitRate,
      cacheSize,
      bucketHitCount: this.bucketHitCount,
      bucketGetCount: this.bucketGetCount,
      bucketHitRate,
      bucketSize,
    };
  }

  public async getRecoveryMotionPlan(): Promise<
    RCL.trajectory_msgs.msg.JointTrajectory[]
  > {
    const recovery_service = new RecoveryService();
    const error = new Error();

    try {
      const response = await recovery_service.call();

      if (!response.success) {
        throw new Error('Recovery service call was not successful');
      }

      return [response.recovery_trajectory];
    } catch (e) {
      error.message = `Recovery motion plan failed: ${e.message}`;
      throw error;
    }
  }

  public clearMotionPlanCache() {
    if (this.motionPlanCache) {
      this.motionPlanCache.reset();
    }

    if (this.motionPlanBucketCache) {
      this.motionPlanBucketCache.clear();
    }

    this.bucketGetCount = 0;
    this.bucketHitCount = 0;
  }

  public getJogAction(
    jogActionRequest: JoggingActionRequest,
    useScalingForSpeedLimits = false,
  ): JoggingAction {
    // Create a copy of the request to avoid modifying the original
    if (useScalingForSpeedLimits) {
      log.info(
        `RosMotionPlanner.getJogAction`,
        'using scaling for speed limits',
      );
    } else {
      log.info(
        `RosMotionPlanner.getJogAction`,
        'using individual joint limits for speed limits',
      );
    }

    const jogAction = new JoggingAction({
      ...jogActionRequest,
      useScalingForSpeedLimits,
    });

    return jogAction;
  }

  public async getToolDirections(
    jointPositions: number[],
    translationDistance: number,
    rotationAngle: number,
    frameOfReference: FrameOfReference,
  ): Promise<FrameDirections> {
    const service = new GetToolDirectionsService(
      jointPositions,
      translationDistance,
      rotationAngle,
      frameOfReference,
    );

    return await service.getResult();
  }
}
